<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-text-sm">
    <div class="tw-flex tw-items-center tw-justify-between">
      <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
        Cast4all <span class="tw-font-normal tw-normal-case tw-text-gray-400">live<span v-if="status?.account"> · {{ ACCOUNT_LABELS[status.account] || status.account }}</span></span>
      </div>
      <button
        class="tw-text-xs tw-text-blue-500 hover:tw-text-blue-700 disabled:tw-text-gray-300"
        :disabled="isFetching"
        @click="refetch()"
      >Vernieuwen</button>
    </div>

    <div v-if="isFetching && !status" class="tw-flex tw-items-center tw-py-2 tw-text-gray-400">
      <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-mr-2" />
      Gegevens ophalen bij Cast4all...
    </div>
    <div v-else-if="error" class="tw-text-red-600 tw-bg-red-50 tw-rounded tw-p-2">
      {{ error.message || 'Cast4all-status ophalen mislukt' }}
    </div>

    <template v-else-if="status">
      <div v-for="(msg, i) in status.warnings" :key="i" class="tw-text-xs tw-text-amber-700 tw-bg-amber-50 tw-rounded tw-p-2">
        {{ msg }}
      </div>

      <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3">
        <!-- What answers "does Cast4all read it": registration, last reading, the communicator -->
        <div class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1">
          <span class="tw-text-gray-500">Registratie</span>
          <span class="tw-font-medium" :class="registeredClass(status.meter.status)">
            {{ status.meter.status || 'Onbekend' }}<span v-if="status.meter.polling === 'No'" class="tw-text-red-600 tw-font-normal"> · uitlezen uit</span>
          </span>
          <span class="tw-text-gray-500">Laatste uitlezing</span>
          <span class="tw-font-medium">
            {{ formatDateTimeNL(status.meter.last_successful_polling_on) }}
            <span v-if="status.meter.last_polling_result && status.meter.last_polling_result !== 'Success'" class="tw-text-red-600 tw-font-normal">({{ status.meter.last_polling_result }})</span>
          </span>
          <template v-if="status.communicator">
            <span class="tw-text-gray-500">Communicator</span>
            <span class="tw-font-medium">
              {{ status.communicator.type || 'Onbekend' }}
              <span :class="registeredClass(status.communicator.status)" class="tw-font-normal">· {{ status.communicator.status || 'onbekend' }}</span>
            </span>
          </template>
          <template v-else-if="status.meter.api_meter">
            <span class="tw-text-gray-500">Uitgelezen via</span>
            <span class="tw-font-medium">API van de omvormer</span>
          </template>
        </div>

        <button
          class="tw-text-xs tw-text-blue-500 hover:tw-text-blue-700 tw-mt-2"
          @click="showDetails = !showDetails"
        >{{ showDetails ? 'Minder' : 'Details' }}</button>

        <div v-if="showDetails" class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1 tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-200">
          <span class="tw-text-gray-500">Uitlezen</span>
          <span class="tw-font-medium">{{ status.meter.polling === 'Yes' ? 'Aan' : status.meter.polling === 'No' ? 'Uit' : (status.meter.polling || 'Onbekend') }}</span>
          <span class="tw-text-gray-500">Laatste comm.</span>
          <span class="tw-font-medium">{{ formatDateTimeNL(status.meter.last_successful_communication_on) }}</span>
          <span class="tw-text-gray-500">Laatste poging</span>
          <span class="tw-font-medium">{{ formatDateTimeNL(status.meter.last_communication_attempt_on) }}</span>
          <template v-if="status.meter.registered_on">
            <span class="tw-text-gray-500">Geregistreerd</span>
            <span class="tw-font-medium">{{ formatDateNL(status.meter.registered_on) }}</span>
          </template>
          <template v-if="status.meter.meter_type || status.meter.type">
            <span class="tw-text-gray-500">Type</span>
            <span class="tw-font-medium">{{ status.meter.meter_type || status.meter.type }}</span>
          </template>
          <template v-if="status.communicator">
            <span class="tw-col-span-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mt-1">Communicator</span>
            <template v-if="status.communicator.logical_device_name">
              <span class="tw-text-gray-500">Apparaat</span>
              <span class="tw-font-mono tw-text-xs tw-self-center">{{ status.communicator.logical_device_name }}</span>
            </template>
            <template v-if="status.communicator.firmware_version">
              <span class="tw-text-gray-500">Firmware</span>
              <span class="tw-font-mono tw-text-xs tw-self-center">{{ status.communicator.firmware_version }}</span>
            </template>
            <span class="tw-text-gray-500">Laatste uitlezing</span>
            <span class="tw-font-medium">
              {{ formatDateTimeNL(status.communicator.last_successful_polling_on) }}
              <span v-if="status.communicator.last_polling_result && status.communicator.last_polling_result !== 'Success'" class="tw-text-red-600 tw-font-normal">({{ status.communicator.last_polling_result }})</span>
            </span>
            <span class="tw-text-gray-500">Laatste comm.</span>
            <span class="tw-font-medium">{{ formatDateTimeNL(status.communicator.last_successful_communication_on) }}</span>
            <template v-if="status.communicator.last_time_sync">
              <span class="tw-text-gray-500">Laatste tijdsync</span>
              <span class="tw-font-medium">{{ formatDateTimeNL(status.communicator.last_time_sync) }}</span>
            </template>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { formatDateNL, formatDateTimeNL } from './formatters'
import { useC4aStatus } from './composables/useC4aStatus'

const props = defineProps({
  serialNumber: { type: String, required: true },
})

const ACCOUNT_LABELS = { staedion: 'Staedion', solarminds: 'Solarminds' }

const { data: status, isFetching, error, refetch } = useC4aStatus(
  computed(() => props.serialNumber), computed(() => true)
)


const registeredClass = (value) => (value === 'Registered' ? 'tw-text-green-600' : 'tw-text-red-600')
// shown in a modal, so everything at once; 'Minder' folds back to the summary
const showDetails = ref(true)
</script>
